import { EndpointTypes } from 'models/endpoint';

// Whether conversation bubbles are tinted per channel. Persisted as the
// boolean customizeChannelColors; this enum is the radio-selection shape.
export const ColorMode = Object.freeze({
  NONE: 'none',
  PER_CHANNEL: 'perChannel',
});

// Selectable per-channel color palette. Names are token names from
// scripts/presentation/themes/tokens.js — no new custom colors are introduced.
// Kept in the application layer (not presentation) because the domain model
// persists these values and the model tests reference them.
export const ChannelColor = Object.freeze({
  GREEN: 'green500',
  PURPLE: 'purple500',
  EGG_SHELLEY_BLUE: 'eggShelleyBlue',
  RED: 'red500',
  BRAND_DARK_BLUE: 'blue700',
  DARK_GREEN: 'green700',
  DARK_PURPLE: 'purple700',
  ORANGE: 'suyderhoudOrange',
  YELLOW: 'yellow500',
  GRAY: 'gray500',
});

export const CHANNEL_COLOR_OPTIONS = Object.freeze([
  { value: ChannelColor.GREEN, label: 'Green' },
  { value: ChannelColor.DARK_GREEN, label: 'Dark Green' },
  { value: ChannelColor.EGG_SHELLEY_BLUE, label: 'Blue' },
  { value: ChannelColor.BRAND_DARK_BLUE, label: 'Dark Blue' },
  { value: ChannelColor.PURPLE, label: 'Purple' },
  { value: ChannelColor.DARK_PURPLE, label: 'Dark Purple' },
  { value: ChannelColor.RED, label: 'Red' },
  { value: ChannelColor.ORANGE, label: 'Orange' },
  { value: ChannelColor.YELLOW, label: 'Yellow' },
  { value: ChannelColor.GRAY, label: 'Gray' },
]);

export const DEFAULT_CHANNEL_COLORS = Object.freeze({
  [EndpointTypes.CHAT]: ChannelColor.GREEN,
  [EndpointTypes.SMS]: ChannelColor.PURPLE,
  [EndpointTypes.EMAIL]: ChannelColor.EGG_SHELLEY_BLUE,
  [EndpointTypes.VOICE]: ChannelColor.RED,
  [EndpointTypes.FB_COMMENT]: ChannelColor.BRAND_DARK_BLUE,
  [EndpointTypes.FB_MESSENGER]: ChannelColor.BRAND_DARK_BLUE,
  [EndpointTypes.WHATSAPP]: ChannelColor.DARK_GREEN,
  [EndpointTypes.INSTAGRAM_DIRECT]: ChannelColor.DARK_PURPLE,
  [EndpointTypes.INSTAGRAM_COMMENT]: ChannelColor.ORANGE,
  [EndpointTypes.TWITTER]: ChannelColor.YELLOW,
  [EndpointTypes.CUSTOM_CHANNEL]: ChannelColor.GRAY,
});

const VALID_COLOR_VALUES = new Set(CHANNEL_COLOR_OPTIONS.map(option => option.value));

// The color token to display/apply for a channel: the agent's saved choice when
// it's still a selectable option, otherwise the channel default. Saved prefs may
// name a token that has since been renamed/removed, so callers must never assume
// channelColors[channel] is valid — go through here instead.
export function resolveChannelColorToken(channelColors, channel) {
  const saved = channelColors && channel in channelColors ? channelColors[channel] : DEFAULT_CHANNEL_COLORS[channel];
  return VALID_COLOR_VALUES.has(saved) ? saved : DEFAULT_CHANNEL_COLORS[channel];
}
